import React from 'react';
import { useTranslation } from 'react-i18next';
import LoginForm from '../components/LoginForm';
import alasAmblem from '../assets/images/alas-beyaz-amblem.svg';
import logoWhite from '../assets/images/logo-white.png';
import { Globe, Check } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// Dil bayrakları
const TrFlag = () => (
  <svg width="20" height="20" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" fill="#E30A17" />
    <circle cx="16" cy="18" r="7" fill="white" />
    <circle cx="18" cy="18" r="6" fill="#E30A17" />
    <path d="M23,18 L25,20 L24,23 L21,21.5 L18,23 L17,20 L19,18" fill="white" />
  </svg>
);

const EnFlag = () => (
  <svg width="20" height="20" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" fill="#00247d" />
    <path d="M0,0 L36,36 M36,0 L0,36" stroke="white" strokeWidth="3.6" />
    <path d="M0,0 L36,36 M36,0 L0,36" stroke="#cf142b" strokeWidth="2.4" />
    <path d="M18,0 L18,36 M0,18 L36,18" stroke="white" strokeWidth="6" />
    <path d="M18,0 L18,36 M0,18 L36,18" stroke="#cf142b" strokeWidth="3.6" />
  </svg>
);

/**
 * Giriş sayfası bileşeni
 * @returns {JSX.Element} - Giriş sayfası
 */
const Login = () => {
  const { t, i18n } = useTranslation();
  
  // Dil değiştirme fonksiyonu
  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-800 to-red-950 p-4 relative overflow-hidden">
      {/* Soyut arka plan desenleri */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Soyut çizgiler */}
        <div className="absolute top-0 left-0 w-full h-full">
          {/* Diyagonal çizgiler */}
          <div className="absolute top-0 right-0 w-full h-[150%] bg-gradient-to-b from-red-500/10 to-transparent skew-y-12 transform -translate-x-1/4"></div>
          <div className="absolute top-0 left-0 w-full h-[150%] bg-gradient-to-b from-red-600/5 to-transparent -skew-y-12 transform translate-x-1/4"></div>
          
          {/* Yatay çizgiler */}
          <div className="absolute top-1/4 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-400/20 to-transparent"></div>
          <div className="absolute top-2/3 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-red-300/15 to-transparent"></div>
          
          {/* Soyut şekiller */}
          <div className="absolute -top-32 -right-32 w-64 h-64 bg-gradient-to-br from-red-500/20 to-red-600/5 rounded-full blur-xl"></div>
          <div className="absolute -bottom-16 -left-16 w-80 h-80 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full blur-xl"></div>
          
          {/* Modern desenler */}
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/20 to-transparent"></div>
          <div className="absolute left-1/2 top-0 w-0.5 h-full bg-gradient-to-b from-red-400/20 via-red-400/5 to-transparent transform -translate-x-1/2"></div>
          
          {/* Hareketli noktalar */}
          <div className="absolute top-10 left-10 w-2 h-2 rounded-full bg-red-400/30"></div>
          <div className="absolute top-[20%] right-[30%] w-3 h-3 rounded-full bg-red-300/20"></div>
          <div className="absolute bottom-[30%] left-[40%] w-4 h-4 rounded-full bg-orange-400/20"></div>
          <div className="absolute bottom-[10%] right-[20%] w-2 h-2 rounded-full bg-red-200/30"></div>
        </div>
      </div>
      
      {/* Dil seçimi - sağ üst köşede */}
      <div className="absolute top-4 right-4 z-20">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 rounded-lg border border-white/20 text-white text-sm transition-colors">
              <Globe className="h-4 w-4" />
              <span>{i18n.language === 'tr' ? 'Türkçe' : 'English'}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40 bg-white/10 backdrop-blur-lg border-white/20">
            <DropdownMenuItem 
              className="text-white hover:bg-white/20 flex items-center justify-between cursor-pointer" 
              onClick={() => changeLanguage('tr')}
            >
              <div className="flex items-center gap-2">
                <TrFlag />
                <span>Türkçe</span>
              </div>
              {i18n.language === 'tr' && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-white hover:bg-white/20 flex items-center justify-between cursor-pointer" 
              onClick={() => changeLanguage('en')}
            >
              <div className="flex items-center gap-2">
                <EnFlag />
                <span>English</span>
              </div>
              {i18n.language === 'en' && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* İçerik */}
      <div className="container mx-auto max-w-5xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-center z-10 px-4 sm:px-6 lg:px-8">
        {/* Sol taraf - Bilgi Alanı */}
        <div className="text-center md:text-left p-6">
          <img 
            src={logoWhite} 
            alt="Alas Fire Logo" 
            className="h-16 md:h-24 w-auto mx-auto md:mx-0 mb-8 drop-shadow-lg" 
          />
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-md">
            {t('login.title')}
          </h1>
          
          <p className="text-lg text-white mb-8 max-w-md mx-auto md:mx-0 drop-shadow">
            {t('login.subtitle')}
          </p>
          
          <div className="flex items-center justify-center md:justify-start space-x-3 text-white text-sm">
            <div className="bg-white/10 p-2 rounded-full backdrop-blur-sm border border-white/10">
              <img 
                src={alasAmblem} 
                alt="Alas Fire Amblem" 
                className="h-5 w-5"
              />
            </div>
            <span>Alas Fire Yangın Söndürme Sistemleri</span>
          </div>
        </div>
        
        {/* Sağ taraf - Login Formu */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 p-6 md:p-8 rounded-xl shadow-2xl">
          <LoginForm />
          
          <div className="mt-8 text-center">
            <p className="text-xs text-white/70">
              {t('login.copyright')} &copy; {new Date().getFullYear()} Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 